import { useEffect, useState } from 'react'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  getFoodEmployeeProfile,
  getFoodEmployeeProfiles,
  getFoodKitchens,
  getFoodMealWindows,
  upsertFoodEmployeeProfile,
} from 'features/food/foodSlice'
import { getUsersSimpleInfo } from 'features/users(Employees)/usersSlice'
import { FoodProvisioningMode } from 'types/api/foodApi.interface'
import { ISelectValue } from 'constants/globalTypes'
import { useHasPermission } from 'hooks/useHasPermission'
import {
  FOOD_PROVISIONING_MODE_OPTIONS,
  foodProvisioningModeLabel,
} from '../foodPlanningData'

const parseBasicUsers = (data: { data: { id: number; fullName: string }[] }) =>
  data.data.map((i) => ({ label: i.fullName, value: i.id }))

export const ProfilesTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const canKitchen = useHasPermission('kitchen')
  const {
    employeeFoodProfiles,
    selectedEmployeeFoodProfile,
    logisticsKitchens,
    mealWindows,
  } = useSelector((store: RootState) => store.food)

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedUserLabel, setSelectedUserLabel] = useState('')
  const [searchEmployee, setSearchEmployee] = useState<ISelectValue | null>(null)
  const [userNames, setUserNames] = useState<{ [userId: number]: string }>({})
  const [stickyKitchenId, setStickyKitchenId] = useState<number | null>(null)
  const [windowModes, setWindowModes] = useState<{ windowDefId: number; mode: number }[]>([])

  useEffect(() => {
    dispatch(getFoodEmployeeProfiles({ page: 1, limit: 200 }))
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
    if (canKitchen) {
      dispatch(getFoodKitchens({ page: 1, limit: 200 }))
    }
  }, [dispatch, canKitchen])

  useEffect(() => {
    const loadNames = async () => {
      const resp = await dispatch(getUsersSimpleInfo({ page: 1, limit: 500 }))
      if (resp.meta.requestStatus !== 'fulfilled') return
      const rows = (resp.payload as any)?.data || []
      const next: { [userId: number]: string } = {}
      rows.forEach((u: any) => {
        next[u.id] = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim()
      })
      setUserNames(next)
    }
    loadNames()
  }, [dispatch])

  const employeeName = (userId: number) => userNames[userId] || `User #${userId}`

  const selectEmployee = (v: ISelectValue) => {
    if (!v?.value) {
      setSearchEmployee(null)
      return
    }
    const userId = Number(v.value)
    const label = String(v.label || '')
    const exists = (employeeFoodProfiles.data || []).some((p) => p.userId === userId)
    setSearchEmployee(v)
    setSelectedUserId(userId)
    setSelectedUserLabel(label)
    if (label) {
      setUserNames((prev) => ({ ...prev, [userId]: label }))
    }
    if (!exists) {
      setStickyKitchenId(null)
      setWindowModes(
        (mealWindows.data || []).map((w) => ({
          windowDefId: w.id,
          mode: FoodProvisioningMode.MessOrHall,
        })),
      )
    }
  }

  useEffect(() => {
    if (!selectedUserId && (employeeFoodProfiles.data || []).length > 0) {
      const first = employeeFoodProfiles.data[0]
      setSelectedUserId(first.userId)
      setSelectedUserLabel(userNames[first.userId] || '')
    }
  }, [employeeFoodProfiles.data, selectedUserId, userNames])

  useEffect(() => {
    if (selectedUserId == null) return
    if (userNames[selectedUserId] && !selectedUserLabel) {
      setSelectedUserLabel(userNames[selectedUserId])
    }
  }, [selectedUserId, userNames, selectedUserLabel])

  useEffect(() => {
    if (selectedUserId == null) return
    const exists = (employeeFoodProfiles.data || []).some((p) => p.userId === selectedUserId)
    if (exists) {
      dispatch(getFoodEmployeeProfile(selectedUserId))
    }
  }, [dispatch, selectedUserId, employeeFoodProfiles.data])

  useEffect(() => {
    if (!selectedEmployeeFoodProfile || selectedEmployeeFoodProfile.userId !== selectedUserId) {
      return
    }
    setStickyKitchenId(selectedEmployeeFoodProfile.stickyKitchenId)
    const defaults = selectedEmployeeFoodProfile.windowModeDefaults || []
    const byWindow = (mealWindows.data || []).map((w) => {
      const existing = defaults.find((d) => d.windowDefId === w.id)
      return {
        windowDefId: w.id,
        mode: existing?.mode ?? FoodProvisioningMode.MessOrHall,
      }
    })
    setWindowModes(
      byWindow.length
        ? byWindow
        : defaults.map((d) => ({ windowDefId: d.windowDefId, mode: d.mode })),
    )
  }, [selectedEmployeeFoodProfile, selectedUserId, mealWindows.data])

  const kitchenOptions = [
    { label: 'None', value: 0 },
    ...(logisticsKitchens.data || []).map((k) => ({ label: k.name, value: k.id })),
  ]

  const handleSave = async () => {
    if (selectedUserId == null) return
    await dispatch(
      upsertFoodEmployeeProfile({
        userId: selectedUserId,
        body: {
          stickyKitchenId: stickyKitchenId || null,
          windowModeDefaults: windowModes,
        },
      }),
    )
    dispatch(getFoodEmployeeProfiles({ page: 1, limit: 200 }))
  }

  const profiles = employeeFoodProfiles.data || []

  return (
    <Flex gap='1rem' width='100%' main>
      <Flex width='40%' minWidth='20rem' column gap='1rem'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Employee Food Profiles</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.75rem'>
            <HonestSelectAsyncInput
              nomarg
              labelText='Employee'
              link='users/basic'
              name='employee'
              parserFunction={parseBasicUsers}
              defaultValue={searchEmployee?.label || ''}
              onChange={selectEmployee}
            />
            {profiles.map((p) => (
              <Card
                key={p.id}
                onClick={() => {
                  setSelectedUserId(p.userId)
                  setSelectedUserLabel(employeeName(p.userId))
                }}
                fullBorder={selectedUserId === p.userId ? colors.orange : undefined}
                hoverBorder
              >
                <CardBody>
                  <BodyText weight='600'>{employeeName(p.userId)}</BodyText>
                  <OtherText grey>
                    {p.windowModeDefaults?.length || 0} window mode
                    {(p.windowModeDefaults?.length || 0) === 1 ? '' : 's'}
                    {p.stickyKitchenId
                      ? ` · Kitchen #${p.stickyKitchenId}`
                      : ' · No sticky kitchen'}
                  </OtherText>
                </CardBody>
              </Card>
            ))}
            {!profiles.length && <OtherText grey>No profiles yet — search an employee above.</OtherText>}
          </ContainerContent>
        </Container>
      </Flex>

      <Flex width='100%'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>
              Edit Profile — {selectedUserId != null ? employeeName(selectedUserId) : 'N/A'}
            </TitleText>
          </ContainerHeader>
          {selectedUserId == null ? (
            <ContainerContent>
              <OtherText grey>Search and select an employee to view or create a profile.</OtherText>
            </ContainerContent>
          ) : (
            <ContainerContent gap='1rem'>
              {canKitchen && (
                <HonestSelectInput
                  nomarg
                  labelText='Sticky kitchen'
                  options={kitchenOptions}
                  defaultValue={stickyKitchenId || 0}
                  onChange={(v: ISelectValue) =>
                    setStickyKitchenId(v && Number(v.value) ? Number(v.value) : null)
                  }
                  name='stickyKitchen'
                />
              )}

              <OtherText grey>WINDOW MODE DEFAULTS (replaces entire set on save)</OtherText>
              {(mealWindows.data || []).map((w) => {
                const current =
                  windowModes.find((m) => m.windowDefId === w.id)?.mode ??
                  FoodProvisioningMode.MessOrHall
                return (
                  <Flex key={w.id} between center gap='1rem' width='100%'>
                    <BodyText>{w.name}</BodyText>
                    <HonestSelectInput
                      nomarg
                      width='14rem'
                      labelText='Mode'
                      options={FOOD_PROVISIONING_MODE_OPTIONS}
                      defaultValue={current}
                      onChange={(v: ISelectValue) =>
                        setWindowModes((prev) => {
                          const next = prev.filter((m) => m.windowDefId !== w.id)
                          return [...next, { windowDefId: w.id, mode: Number(v.value) }]
                        })
                      }
                      name={`mode-${w.id}`}
                    />
                  </Flex>
                )
              })}

              {selectedEmployeeFoodProfile?.windowModeDefaults?.length > 0 && (
                <Flex wrap gap='0.25rem'>
                  {selectedEmployeeFoodProfile.windowModeDefaults.map((d) => (
                    <StatusPill key={d.id} status='info'>
                      {`${d.windowDefName}: ${foodProvisioningModeLabel(d.mode)}`}
                    </StatusPill>
                  ))}
                </Flex>
              )}

              <Button variant='primary' onClick={handleSave}>
                Save profile
              </Button>
            </ContainerContent>
          )}
        </Container>
      </Flex>
    </Flex>
  )
}
